import { API_STATUS, DOC_STATUS } from "src/config/constants/genericConstants";
import {
  getFormatedDate,
  getGMRLastSaturdayDate,
  isAfterDate
} from "src/utils/getDates";
import { getAdjustedItems } from "src/utils/inventoryUtils";
import stringToNumber from "src/utils/stringToNumber";
import {
  PhysicalInventoryData,
  PhysicalInventoryIntitialState
} from "../interface";
import { getFirstDefined } from "src/utils/genericFunctions";

export const initialState: PhysicalInventoryIntitialState = {
  adjustment: {
    valuation: {
      id: "valuation",
      isSeparated: true,
      noAdornment: true,
      type: "text",
      value: "Retail",
      disabled: true,
      hideInput: true
    },
    grocery: {
      id: "grocery",
      value: "",
      testId: "grocery-input"
    },
    frozen: {
      id: "frozen",
      value: ""
    },
    deli: {
      id: "deli",
      value: ""
    },
    beerWine: {
      id: "beerWine",
      value: ""
    },
    hbc: {
      id: "hbc",
      value: ""
    },
    gm: {
      id: "gm",
      value: ""
    },
    produce: {
      id: "produce",
      value: ""
    },
    meat: {
      id: "meat",
      isSeparated: true,
      value: ""
    },
    total: {
      id: "total",
      value: "",
      disabled: true
    }
  },
  notes: "",
  approverNotes: "",
  attachments: [],
  blockNavigation: true,
  loading: false,
  physicalInventoryDate: {
    masterData: [],
    effectiveDate: [],
    gmrEffectiveDate: [],
    gmrMaxDate: undefined,
    isStaticGMRDataGenerated: false,
    status: API_STATUS.IDLE,
    silentApiTrigger: false
  }
};

export const handleUpdatePhysicalInventoryNavigationBlocking = (
  state: PhysicalInventoryIntitialState,
  action: {
    payload: boolean;
  }
) => {
  state.blockNavigation = action.payload;
};

export const handleUpdatePhysicalInventoryAdjustment = (
  state: PhysicalInventoryIntitialState,
  action: {
    payload: {
      total: {
        value: any;
      };
    };
  }
) => {
  const data = action.payload;
  data.total.value = 0;

  Object.values(data).forEach((item: any) => {
    if (item.id === "total" || item.id === "valuation") return;
    data.total.value += getFirstDefined(stringToNumber(item.value ?? ""), 0);
  });

  data.total.value = data.total.value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  state.adjustment = data;
};

export const handleUpdatePhysicalInventoryNotes = (
  state: PhysicalInventoryIntitialState,
  action: { payload: string }
) => {
  const notes = action.payload;
  state.notes = notes;
};

export const handleUpdatePhysicalInventoryApproverNotes = (
  state: PhysicalInventoryIntitialState,
  action: { payload: string }
) => {
  state.approverNotes = action.payload;
};

export const handleUpdatePhysicalInventoryApproverAttachment = (
  state: PhysicalInventoryIntitialState,
  action: {
    payload: { fileName: string; fileURL: string; fileType: string };
  }
) => {
  const isDuplicate = state.attachments.some(
    (attachment) => attachment.fileURL === action.payload.fileURL
  );
  if (!isDuplicate) {
    state.attachments = [...state.attachments, action.payload];
  }
};

export const handleRemovePhysicalInventoryApproverAttachment = (
  state: PhysicalInventoryIntitialState,
  action: { payload: number }
) => {
  const filterAttachments = state.attachments.filter(
    (_item: any, itemIndex: number) => itemIndex !== action?.payload
  );
  state.attachments = filterAttachments;
};

export const handleEditPhysicalInventoryAdjustment = (
  state: PhysicalInventoryIntitialState,
  action: {
    payload: {
      id: string | number;
      value: any;
    };
  }
) => {
  const { id, value } = action.payload;
  const creditItems = state?.adjustment;
  const input = value === "$0-" ? "-" : value;
  const newEditedAdjItems = getAdjustedItems(id as string, input, creditItems);
  if (newEditedAdjItems) state.adjustment = newEditedAdjItems;
};

export const handleActivateSilentPiDateApiTrigger = (
  state: any,
  action: {
    payload: any;
  }
) => {
  state.physicalInventoryDate.silentApiTrigger = action.payload;
};

export const handleResetPhysicalInventory = () => {
  return initialState;
};

export const handleCreatePhysicalInventoryPending = (state: any) => {
  state.loading = true;
};

export const handleCreatePhysicalInventoryFulfilled = (state: any) => {
  state.loading = false;
};

export const handleCreatePhysicalInventoryRejected = (state: any) => {
  state.loading = false;
};

export const handlePhysicalInventoryApproverPending = (state: any) => {
  state.loading = true;
};

export const handlePhysicalInventoryApproverFulfilled = (state: any) => {
  state.loading = false;
};

export const handlePhysicalInventoryApproverRejected = (state: any) => {
  state.loading = false;
};
export const handleCreatePhysicalInventoryIAUPending = (state: any) => {
  state.loading = true;
};

export const handleCreatePhysicalInventoryIAUFulfilled = (state: any) => {
  state.loading = false;
};

export const handleCreatePhysicalInventoryIAURejected = (state: any) => {
  state.loading = false;
};

export const handleFetchPhysicalInventoryDatePending = (state: any) => {
  state.physicalInventoryDate.status = API_STATUS.PENDING;
};

/**
 * Processes a list of physical inventory records and extracts relevant dates.
 *
 * This function determines the latest effective dates for physical inventory,
 * identifies if static GMR (Gross Margin Report) data is generated, and
 * finds the latest reconciled GMR date.
 *
 * **Functionality:**
 * - Extracts effectiveDate for each inventory item.
 * - Collects gmrEffectiveDate for historical or static GMR data.
 * - Determines gmrMaxDate, which is the latest effective date among static GMR data.
 * - Checks if isStaticGMRDataGenerated is true, meaning new static GMR data exists.
 *
 * @param {PhysicalInventoryData[]} data - The array of physical inventory records.
 * @returns {{
 *   effectiveDate: string[],      // List of all effective dates
 *   gmrEffectiveDate: string[],   // List of GMR effective dates
 *   gmrMaxDate: string,           // Latest effective date among static GMR data
 *   isStaticGMRDataGenerated: boolean // Flag indicating if static GMR data exists
 * }} An object containing formatted inventory dates and status flags.
 */
export const getFormatedPhysicalInventaryDate = (
  data: PhysicalInventoryData[]
) => {
  const gmrEffectiveDate: string[] = [];
  let isStaticGMRDataGenerated = false;

  const latestPI = data?.[0];
  if (latestPI?.status !== DOC_STATUS.RECONCILED) {
    isStaticGMRDataGenerated = true;
  }
  let gmrMaxDate = getGMRLastSaturdayDate();

  let maxDateSet = false;
  const formatData = (item: PhysicalInventoryData) => {
    if (item.isHistoricalPhysicalInventory || item.isStaticGMRDataGenerated) {
      gmrEffectiveDate.push(item.effectiveDate);
    }

    if (
      !maxDateSet &&
      item.isStaticGMRDataGenerated &&
      isAfterDate(getFormatedDate(item.effectiveDate), gmrMaxDate)
    ) {
      gmrMaxDate = getFormatedDate(item.effectiveDate);
      maxDateSet = true;
    }

    return item.effectiveDate;
  };
  return {
    effectiveDate: data.map(formatData),
    gmrEffectiveDate,
    gmrMaxDate,
    isStaticGMRDataGenerated
  };
};

export const handleFetchPhysicalInventoryDateFulfilled = (
  state: PhysicalInventoryIntitialState,
  action: any
) => {
  const physicalInventoryDatesData = getFirstDefined(action.payload?.data, []);
  const {
    effectiveDate,
    gmrEffectiveDate,
    isStaticGMRDataGenerated,
    gmrMaxDate
  } = getFormatedPhysicalInventaryDate(physicalInventoryDatesData);
  state.physicalInventoryDate = {
    masterData: physicalInventoryDatesData,
    effectiveDate,
    gmrEffectiveDate,
    gmrMaxDate,
    isStaticGMRDataGenerated,
    status: API_STATUS.IDLE,
    silentApiTrigger: state.physicalInventoryDate.silentApiTrigger
  };
  state.loading = false;
};

export const handleFetchPhysicalInventoryDateRejected = (state: any) => {
  state.loading = false;
  state.physicalInventoryDate = {
    masterData: [],
    effectiveDate: [],
    gmrEffectiveDate: [],
    gmrMaxDate: undefined,
    isStaticGMRDataGenerated: false,
    status: API_STATUS.ERROR
  };
};

export const resetPhysicalInventoryDate = (state: any) => {
  state.physicalInventoryDate = {
    masterData: [],
    effectiveDate: [],
    gmrEffectiveDate: [],
    gmrMaxDate: undefined,
    isStaticGMRDataGenerated: false,
    status: API_STATUS.PENDING
  };
};
